import { useState } from "react";
import { Check, Crown, Rocket } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const tiers = [
  {
    name: "Free", icon: Rocket, highlight: false,
    price: { usd: { monthly: "$0", yearly: "$0" }, inr: { monthly: "₹0", yearly: "₹0" } },
    period: { monthly: "forever", yearly: "forever" },
    desc: "Start exploring the marketplace.",
    features: ["Browse all 1-minute pitches", "Flip cards to view pitch decks", "Engage with hearts, dollars & likes", "Weekly rankings access", "Newsletter with top pitches"],
    cta: "Get Started Free",
  },
  {
    name: "Investor Pro", icon: Crown, highlight: true,
    price: { usd: { monthly: "$49", yearly: "$470" }, inr: { monthly: "₹3,999", yearly: "₹38,999" } },
    period: { monthly: "/month", yearly: "/year" },
    desc: "For serious investors who want first access.",
    features: ["Everything in Free", "Join live webinars with founders", "Priority seats at Shark Tank events", "Direct messaging with founders", "Early access to new pitches", "Deal-flow analytics dashboard"],
    cta: "Go Premium",
  },
  {
    name: "Founder", icon: Rocket, highlight: false,
    price: { usd: { monthly: "$99", yearly: "$950" }, inr: { monthly: "₹7,999", yearly: "₹76,999" } },
    period: { monthly: "/month", yearly: "/year" },
    desc: "For startups raising their next round.",
    features: ["Upload unlimited 1-minute pitches", "Full pitch deck hosting", "Featured placement eligibility", "Pitch at live Shark Tank events", "Investor engagement insights", "Dedicated founder success manager"],
    cta: "Start Raising",
  },
];

export default function Pricing() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [currency, setCurrency] = useState("usd");
  const [billing, setBilling] = useState("monthly");

  const onCta = async (tier) => {
    if (tier.name === "Investor Pro") {
      if (!user) { toast.info("Login or sign up first to go Premium."); navigate("/login"); return; }
      if (user.is_premium) { toast.success("You're already a Premium member!"); return; }
      try {
        await api.post("/users/upgrade");
        await refreshUser();
        toast.success("Premium unlocked! (demo mode — payments coming soon)");
      } catch (e) {
        toast.error("Upgrade failed. Please try again.");
      }
      return;
    }
    if (tier.name === "Free") {
      if (user) toast.success("You already have full free access!");
      else navigate("/signup");
      return;
    }
    toast.info("Founder plans launch soon — you're on the early access list!");
  };

  return (
    <main className="relative pt-28 pb-24 max-w-6xl mx-auto px-4 sm:px-6">
      <div className="noise-overlay" />
      <div className="text-center mb-14">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
          Simple, <span className="gold-gradient-text">Freemium</span> Pricing
        </h1>
        <p className="mt-4 text-zinc-500 text-sm md:text-base max-w-md mx-auto">Start free. Upgrade when you're ready for premium access and full functionality.</p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <div className="inline-flex rounded-full border border-white/15 p-1" data-testid="currency-toggle">
            {[["usd", "$ USD"], ["inr", "₹ INR"]].map(([key, label]) => (
              <button
                key={key}
                data-testid={`currency-${key}`}
                onClick={() => setCurrency(key)}
                className={`px-5 py-2 rounded-full text-xs font-bold transition-colors ${currency === key ? "bg-[#F5B301] text-black" : "text-zinc-400 hover:text-white"}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-full border border-white/15 p-1" data-testid="billing-toggle">
            {[["monthly", "Monthly"], ["yearly", "Yearly"]].map(([key, label]) => (
              <button
                key={key}
                data-testid={`billing-${key}`}
                onClick={() => setBilling(key)}
                className={`px-5 py-2 rounded-full text-xs font-bold transition-colors flex items-center gap-1.5 ${billing === key ? "bg-[#F5B301] text-black" : "text-zinc-400 hover:text-white"}`}
              >
                {label}
                {key === "yearly" && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${billing === "yearly" ? "bg-black text-[#F5B301]" : "bg-[#F5B301]/15 text-[#F5B301]"}`}>SAVE 20%</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-8" data-testid="pricing-tiers">
        {tiers.map((t) => (
          <div
            key={t.name}
            data-testid={`pricing-tier-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
            className={`relative rounded-2xl p-8 text-left flex flex-col gold-glow-hover ${
              t.highlight ? "bg-[#141210] border-2 border-[#F5B301]/60" : "bg-[#0d0d0d] border border-white/5"
            }`}
          >
            {t.highlight && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F5B301] text-black text-[10px] font-bold uppercase tracking-widest px-4 py-1 rounded-full">
                Most Popular
              </span>
            )}
            <t.icon size={26} className="text-[#F5B301] mb-4" />
            <h2 className="text-xl font-semibold text-white">{t.name}</h2>
            <p className="mt-1 text-xs text-zinc-500">{t.desc}</p>
            <p className="mt-5" data-testid={`price-${t.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <span className="font-heading font-bold text-4xl text-white">{t.price[currency][billing]}</span>
              <span className="text-sm text-zinc-500 ml-1">{t.period[billing]}</span>
            </p>
            <ul className="mt-6 space-y-3 flex-1">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-300">
                  <Check size={15} className="text-[#F5B301] mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <button
              data-testid={`pricing-cta-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => onCta(t)}
              className={`mt-8 w-full py-3 rounded-full text-sm font-bold transition-colors ${
                t.highlight ? "bg-[#F5B301] text-black hover:bg-[#D99E01]" : "border border-[#F5B301]/50 text-[#F5B301] hover:bg-[#F5B301]/10"
              }`}
            >
              {t.cta}
            </button>
          </div>
        ))}
      </div>
      <p className="mt-10 text-center text-xs text-zinc-600">Premium payments are coming soon. All plans shown are UI preview only.</p>
    </main>
  );
}
